import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiTypography,
} from "../../components";
import { useSiteCore } from "../../hooks/useSiteCore";
import { gridSize } from "../../helpers/constants";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { userPreferredLanguageSelector } from "../../redux/slices/appState/appStateSelector";
import { ManageLocaleLanguage } from "./Models";
import { ManageLocaleWrapper } from "./ManageLocale.style";
import { InformationBadge } from "../../common/informationBadge/informationBadge";
import { LanguageDropDownData } from "../../api/services/pageContent/models";
import { getValueOrDash } from "../../helpers/utils";
import { Locale } from "../../api/services/common/models";

interface ViewManageLocaleProps {
  show: boolean;
  handleCloseModal: () => void;
  localeLanguages: Locale[];
  title: string;
  bannerInfoMessage?: string;
}

export const ViewManageLocale: FC<ViewManageLocaleProps> = ({
  show,
  handleCloseModal,
  localeLanguages,
  title,
  bannerInfoMessage,
}) => {
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const { commonLabels } = useSiteCore();
  const { fetchLanguageDropdownValues } = useNavigationSiteCore();
  const languagesFormDetails = useMemo(() => {
    const list = [] as ManageLocaleLanguage[];
    const siteCoreLanguages = fetchLanguageDropdownValues();
    siteCoreLanguages.forEach((language: LanguageDropDownData) => {
      const localeLang = localeLanguages.find(
        (el) => el.localeKey === language.value,
      );
      const obj = {
        label: language.label,
        value: getValueOrDash(localeLang?.name) ?? "",
      };
      if (language.value === userSelectedLanguage) {
        list.unshift(obj);
      } else {
        list.push(obj);
      }
    });
    return list;
  }, [localeLanguages]);

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={title}
      hasLargeContent
      secondaryButtonTitle={commonLabels.back}
      onSecondaryButtonClick={handleCloseModal}
      className="manage-local-modal"
    >
      <>
        {bannerInfoMessage && <InformationBadge message={bannerInfoMessage} />}

        <ManageLocaleWrapper>
          {
            <MuiTypography
              variant="h2SemiBold"
              component="h2"
              className="text-title"
            >
              {`${commonLabels.viewLocale} - ${languagesFormDetails[0]?.value ?? ""}`}
            </MuiTypography>
          }
          <MuiGrid container>
            {languagesFormDetails.map(({ value, label }) => (
              <MuiGrid key={label} size={gridSize}>
                <MuiDoubleLabel label={label} value={value} />
              </MuiGrid>
            ))}
          </MuiGrid>
        </ManageLocaleWrapper>
      </>
    </MuiModal>
  );
};
